import { AccountDateTimePicker as DateTimePicker } from '../date-time-picker';
/**
 * Calendar settings (DESIGN.md T5 in a dialog): view (week start, time format, time scale, week numbers, declined),
 * working time (days, hours, fit to screen), new events (length, reminder) and time zones. Every change applies at
 * once and is stored for the user by the platform API (settings.ts).
 */
import { useI18n } from '@platform/i18n';
import { Button, Checkbox, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, MenuSelect, SegmentedControl, Tabs, TabsContent, TabsList, TabsTrigger } from '@platform/ui';
import { useId, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import type { CalendarSettings } from '../api/calendar-extra';
import { useCalendar } from './calendar-context';
import { useCalendarFormat } from './format';
import { TIME_SCALES, timeScaleOf, type TimeScale } from './settings';
import { ColorChoice } from './color-choice';
import { CalendarPlusIcon, ClockIcon, GlobeIcon, SettingsIcon, TagsIcon, Trash2Icon, type LucideIcon } from 'lucide-react';
import { browserTimeZone } from './zones';
import { TimeZonePicker } from '../time-zone-picker';

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
const DURATIONS = [15, 30, 45, 60, 90, 120] as const;
const REMINDERS = [null, 0, 5, 10, 15, 30, 60, 120, 1440] as const;

const pad = (n: number) => String(n).padStart(2, '0');
const toTime = (minutes: number) => `${pad(Math.floor(minutes / 60) % 24)}:${pad(minutes % 60)}`;
const fromTime = (value: string): number | undefined => {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  return match ? Number(match[1]) * 60 + Number(match[2]) : undefined;
};

function Row({ label, htmlFor, children }: { label: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      {htmlFor ? (
        <Label htmlFor={htmlFor} className="font-normal">
          {label}
        </Label>
      ) : (
        <span className="text-sm">{label}</span>
      )}
      {children}
    </div>
  );
}

/** Opt-in for desktop notifications of reminders (browser permission). */
function DesktopNotifications() {
  const { t } = useI18n();
  const supported = typeof Notification !== 'undefined';
  const [permission, setPermission] = useState(supported ? Notification.permission : 'denied');
  if (!supported) return null;
  if (permission === 'granted') return <p className="text-xs text-muted-foreground">{t('webmail.calendarUx.reminders.notificationsOn')}</p>;
  if (permission === 'denied') return <p className="text-xs text-muted-foreground">{t('webmail.calendarUx.reminders.notificationsBlocked')}</p>;
  return (
    <Button type="button" variant="outline" size="sm" className="self-start" onClick={() => void Notification.requestPermission().then(setPermission)}>
      {t('webmail.calendarUx.reminders.notificationsAllow')}
    </Button>
  );
}

/** The five settings sections, shared by the dialog (tabs) and the settings page (section switcher). `open` resets the working-hours draft. */
function useCalendarSettingsSections(open: boolean): { id: string; label: string; icon: LucideIcon; content: ReactNode }[] {
  const { t } = useI18n();
  const format = useCalendarFormat();
  const { settings, updateSettings, categories, saveCategories } = useCalendar();
  const ids = { start: useId(), end: useId(), days: useId(), display: useId(), secondary: useId() };
  const set = (patch: Partial<CalendarSettings>) => updateSettings(patch);
  const scale = timeScaleOf(settings.hourHeight);
  const dayName = (index: number) => format.weekday(new Date(2026, 0, 4 + index));
  const longDayName = (index: number) => new Intl.DateTimeFormat(format.locale, { weekday: 'long' }).format(new Date(2026, 0, 4 + index));
  const [hoursError, setHoursError] = useState(false);
  const [hoursDraft, setHoursDraft] = useState(() => ({ start: toTime(settings.workingStartMinutes), end: toTime(settings.workingEndMinutes) }));
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) {
      setHoursDraft({ start: toTime(settings.workingStartMinutes), end: toTime(settings.workingEndMinutes) });
      setHoursError(false);
    }
  }
  const setHours = (field: 'workingStartMinutes' | 'workingEndMinutes', value: string) => {
    const minutes = fromTime(value);
    if (minutes === undefined) return;
    const next = { ...settings, [field]: field === 'workingEndMinutes' && minutes === 0 ? 1440 : minutes };
    setHoursError(next.workingEndMinutes <= next.workingStartMinutes);
    if (next.workingEndMinutes > next.workingStartMinutes) set({ [field]: next[field] });
  };

  const check = (key: 'fitWorkingHours' | 'weekNumbers' | 'hideDeclined' | 'remindersEnabled', label: string) => (
    <label className="flex items-center gap-2 text-sm">
      <Checkbox checked={settings[key]} onCheckedChange={(v) => set({ [key]: v === true })} />
      {label}
    </label>
  );

  return [
    {
      id: 'view',
      label: t('webmail.calendarUx.settings.sectionView'),
      icon: SettingsIcon,
      content: (
        <>
            <h3 className="text-sm font-semibold">{t('webmail.calendarUx.settings.sectionView')}</h3>
            <Row label={t('webmail.calendarUx.settings.weekStart')}>
              <MenuSelect
                label={t('webmail.calendarUx.settings.weekStart')}
                value={settings.weekStart === null ? 'locale' : String(settings.weekStart)}
                options={[{ value: 'locale', label: t('webmail.calendarUx.settings.weekStartLocale') }, ...WEEKDAYS.map((_, i) => ({ value: String(i), label: longDayName(i) }))]}
                onChange={(v) => set({ weekStart: v === 'locale' ? null : Number(v) })}
              />
            </Row>
            <Row label={t('webmail.calendarUx.settings.timeFormat')}>
              <MenuSelect
                label={t('webmail.calendarUx.settings.timeFormat')}
                value={settings.timeFormat}
                options={[
                  { value: 'locale', label: t('webmail.calendarUx.settings.timeFormatLocale') },
                  { value: 'h12', label: t('webmail.calendarUx.settings.timeFormat12') },
                  { value: 'h24', label: t('webmail.calendarUx.settings.timeFormat24') },
                ]}
                onChange={(v) => set({ timeFormat: v })}
              />
            </Row>
            <SegmentedControl<TimeScale | 'custom'>
              label={t('webmail.calendarUx.settings.timeScale')}
              value={scale ?? 'custom'}
              options={[
                { value: 'compact', label: t('webmail.calendarUx.settings.timeScaleCompact') },
                { value: 'normal', label: t('webmail.calendarUx.settings.timeScaleNormal') },
                { value: 'large', label: t('webmail.calendarUx.settings.timeScaleLarge') },
                ...(scale ? [] : [{ value: 'custom' as const, label: t('webmail.calendarUx.settings.timeScaleCustom') }]),
              ]}
              onChange={(v) => v !== 'custom' && set({ hourHeight: TIME_SCALES[v], fitWorkingHours: false })}
            />
            <p className="text-xs text-muted-foreground">{t('webmail.calendarUx.settings.timeScaleHint')}</p>
            {check('weekNumbers', t('webmail.calendarUx.settings.weekNumbers'))}
            {check('hideDeclined', t('webmail.calendarUx.settings.hideDeclined'))}
            <Row label={t('webmail.calendarUx.settings.customDays')} htmlFor={ids.days}>
              <Input id={ids.days} type="number" min={2} max={14} className="w-20" value={settings.customDays} onChange={(e) => { const n = Number(e.target.value); if (Number.isInteger(n) && n >= 2 && n <= 14) set({ customDays: n }); }} />
            </Row>
                  </>
      ),
    },
    {
      id: 'working',
      label: t('webmail.calendarUx.settings.sectionWorkingTime'),
      icon: ClockIcon,
      content: (
        <>
            <h3 className="text-sm font-semibold">{t('webmail.calendarUx.settings.sectionWorkingTime')}</h3>
            <fieldset className="flex flex-col gap-1.5">
              <legend className="mb-1.5 text-sm">{t('webmail.calendarUx.settings.workingDays')}</legend>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((day, i) => {
                  const index = (format.weekStart + i) % 7;
                  const name = WEEKDAYS[index] ?? day;
                  const on = settings.workingDays.includes(name);
                  return (
                    <Button
                      key={name}
                      type="button"
                      size="sm"
                      variant="outline"
                      role="checkbox"
                      aria-checked={on}
                      aria-label={longDayName(index)}
                      className={on ? 'border-primary bg-primary/10 text-primary hover:bg-primary/15' : undefined}
                      onClick={() => set({ workingDays: on ? settings.workingDays.filter((d) => d !== name) : WEEKDAYS.filter((d) => d === name || settings.workingDays.includes(d)) })}
                    >
                      {dayName(index)}
                    </Button>
                  );
                })}
              </div>
            </fieldset>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={ids.start} className="font-normal">
                  {t('webmail.calendarUx.settings.workingStart')}
                </Label>
                <DateTimePicker id={ids.start} mode="time" formatTime={format.time} step={900} value={hoursDraft.start} onValueChange={(value) => { setHoursDraft((draft) => ({ ...draft, start: value })); setHours('workingStartMinutes', value); }} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={ids.end} className="font-normal">
                  {t('webmail.calendarUx.settings.workingEnd')}
                </Label>
                <DateTimePicker id={ids.end} mode="time" formatTime={format.time} step={900} value={hoursDraft.end} onValueChange={(value) => { setHoursDraft((draft) => ({ ...draft, end: value })); setHours('workingEndMinutes', value); }} />
              </div>
            </div>
            {hoursError && (
              <p role="alert" className="text-xs text-destructive">
                {t('webmail.calendarUx.settings.workingInvalid')}
              </p>
            )}
            {check('fitWorkingHours', t('webmail.calendarUx.settings.fitWorkingHours'))}
                  </>
      ),
    },
    {
      id: 'events',
      label: t('webmail.calendarUx.settings.sectionNewEvents'),
      icon: CalendarPlusIcon,
      content: (
        <>
            <h3 className="text-sm font-semibold">{t('webmail.calendarUx.settings.sectionNewEvents')}</h3>
            <Row label={t('webmail.calendarUx.settings.defaultDuration')}>
              <MenuSelect
                label={t('webmail.calendarUx.settings.defaultDuration')}
                value={String(settings.defaultDurationMinutes)}
                options={DURATIONS.map((m) => ({ value: String(m), label: format.duration(new Date(0), new Date(m * 60_000), false) }))}
                onChange={(v) => set({ defaultDurationMinutes: Number(v) })}
              />
            </Row>
            <Row label={t('webmail.calendarUx.settings.defaultReminder')}>
              <MenuSelect
                label={t('webmail.calendarUx.settings.defaultReminder')}
                value={String(settings.defaultReminderMinutes)}
                options={REMINDERS.map((m) => ({ value: String(m), label: m === null ? t('webmail.calendar.editor.noReminder') : t('webmail.calendar.reminderMinutes', { n: m }) }))}
                onChange={(v) => set({ defaultReminderMinutes: v === 'null' ? null : Number(v) })}
              />
            </Row>
            {check('remindersEnabled', t('webmail.calendarUx.settings.reminders'))}
            {settings.remindersEnabled && <DesktopNotifications />}
                  </>
      ),
    },
    {
      id: 'categories',
      label: t('webmail.calendarUx.categorize.manage'),
      icon: TagsIcon,
      content: (
        <>
              <h3 className="text-sm font-semibold">{t('webmail.calendarUx.categorize.manage')}</h3>
              {categories.length === 0 && <p className="text-sm text-muted-foreground">{t('webmail.calendarUx.settings.noCategories')}</p>}
              {categories.map((c) => (
                <div key={c.name} className="flex flex-wrap items-center gap-2">
                  <span className="min-w-24 flex-1 truncate text-sm">{c.name}</span>
                  <ColorChoice small label={t('webmail.calendarUx.categorize.colorOf', { name: c.name })} value={c.color} onChange={(color) => color && saveCategories(categories.map((x) => (x === c ? { ...x, color } : x)))} />
                  <Button size="icon-sm" variant="ghost" aria-label={t('webmail.calendarUx.categorize.remove', { name: c.name })} onClick={() => saveCategories(categories.filter((x) => x !== c))}>
                    <Trash2Icon />
                  </Button>
                </div>
              ))}
                  </>
      ),
    },
    {
      id: 'zones',
      label: t('webmail.calendarUx.settings.sectionTimeZones'),
      icon: GlobeIcon,
      content: (
        <>
            <h3 className="text-sm font-semibold">{t('webmail.calendarUx.settings.sectionTimeZones')}</h3>
            <TimeZonePicker id={ids.display} label={t('webmail.calendarUx.settings.displayZone')} value={settings.displayTimeZone} onChange={(zone) => set({ displayTimeZone: zone })} />
            <p className="text-xs text-muted-foreground">{t('webmail.calendarUx.settings.displayZoneBrowser', { zone: browserTimeZone() })}</p>
            <TimeZonePicker id={ids.secondary} label={t('webmail.calendarUx.settings.secondaryZone')} value={settings.secondaryTimeZone} onChange={(zone) => set({ secondaryTimeZone: zone })} fallbackZone={null} resetLabel={t('webmail.regional.hideSecondaryZone')} emptyLabel={t('webmail.regional.noSecondaryZone')} />
                  </>
      ),
    },
  ];
}

export function CalendarSettingsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useI18n();
  const sections = useCalendarSettingsSections(open);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent framed size="lg" className="max-h-full flex-col gap-4">
        <DialogHeader>
          <DialogTitle>{t('webmail.calendarUx.settings.title')}</DialogTitle>
          <DialogDescription>{t('webmail.calendarUx.settings.description')}</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="view" className="flex min-h-0 flex-1 flex-col">
          <TabsList aria-label={t('webmail.calendarUx.settings.title')} className="w-full [&_span]:sm:not-sr-only">
            {sections.map(({ id, label, icon }) => (
              <TabsTrigger key={id} value={id} icon={icon} label={label} />
            ))}
          </TabsList>
          <DialogBody className="min-h-0 flex-1 overflow-y-auto pr-1">
            {sections.map(({ id, content }) => (
              <TabsContent key={id} value={id} className="flex flex-col gap-3">
                {content}
              </TabsContent>
            ))}
          </DialogBody>
        </Tabs>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>{t('webmail.calendarUx.settings.done')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The same settings as a page body (Settings > Calendar & contacts): one section at a time, chosen with
 * the section menu and kept in the URL (`?section=`), so the calendar gear and the settings page share
 * one implementation and one persistence.
 */
export function CalendarSettingsPanel() {
  const { t } = useI18n();
  const sections = useCalendarSettingsSections(true);
  const [params, setParams] = useSearchParams();
  const active = sections.find(({ id }) => id === params.get('section')) ?? sections[0]!;
  return (
    <div className="flex max-w-xl flex-col gap-4">
      <MenuSelect
        label={t('ia.webmailAreas.sections')}
        value={active.id}
        options={sections.map(({ id, label }) => ({ value: id, label }))}
        onChange={(id) => setParams(id === sections[0]!.id ? {} : { section: id }, { replace: true })}
        className="w-full sm:w-72"
      />
      <div className="flex flex-col gap-3">{active.content}</div>
      <p className="text-xs text-muted-foreground">{t('ia.webmailAreas.calendarHint')}</p>
    </div>
  );
}
